/**
 * `<CommandPalette>` — the ⌘K / Ctrl-K global navigation + action launcher (frontend.md
 * §2.1, §1.1 keyboard-first).
 *
 * Two layers compose into one keyboard-navigable list:
 *
 *  1. **Commands** — the per-surface "Go to X" navigations registered into the `ShellMount`
 *     by `surfaceRegistry` / Agent 9, PLUS a set of static actions defined here (New note,
 *     New chat, Clear chat history, theme switches, Toggle sidebar) and a dynamic
 *     "Search the corpus for '<query>'" entry. These are fuzzy-matched against the input.
 *  2. **Live results** — when the query is non-empty, we query the corpus (debounced) and
 *     show grouped matches: Notes (by title), Tags (by prefix), Entities (by prefix).
 *     Selecting a result navigates to the relevant surface.
 *
 * Open state lives in `useUiStore.commandPaletteOpen`; this component installs the global
 * ⌘K / Ctrl-K key handler. Up/Down move through ALL items (commands + live results), Enter
 * runs/navigates, Esc closes.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useUiStore } from '@/stores';
import { THEME_CATALOG } from '@/theme/themeCatalog';
import { listEntities, listTags, noteTitles } from '@/api';
import { clearChatHistory } from '@/chat/chatApi';
import { useToastStore } from '@/hooks';
import { ROUTES, toEditor, toSearch, toSearchTag } from '@/routes';
import { useShellMountSnapshot } from './ShellMountContext';
import type { PaletteCommand } from './ShellMount';

/** Per-group cap on live results — keeps the list scannable. */
const RESULTS_PER_GROUP = 8;
/** Debounce for the live corpus queries — typing is cheap, the network is not. */
const LIVE_DEBOUNCE_MS = 175;
/** A single page is plenty for a quick-search; notes are filtered client-side. */
const LIVE_FETCH_LIMIT = 1000;

/** A simple subsequence fuzzy score — higher is better; -1 means no match. */
function fuzzyScore(needle: string, haystack: string): number {
  if (needle.length === 0) return 0;
  const n = needle.toLowerCase();
  const h = haystack.toLowerCase();
  let hi = 0;
  let score = 0;
  let streak = 0;
  for (let ni = 0; ni < n.length; ni += 1) {
    const ch = n[ni];
    const found = h.indexOf(ch as string, hi);
    if (found === -1) return -1;
    streak = found === hi ? streak + 1 : 0;
    score += 1 + streak;
    hi = found + 1;
  }
  return score;
}

function commandMatchScore(cmd: PaletteCommand, query: string): number {
  if (query.trim().length === 0) return 0;
  const haystacks = [cmd.label, cmd.group ?? '', ...(cmd.keywords ?? [])];
  let best = -1;
  for (const h of haystacks) {
    const s = fuzzyScore(query, h);
    if (s > best) best = s;
  }
  return best;
}

// Remaster U1: theme commands render from the shared catalog (theme/themeCatalog.ts).
// The old local list had drifted — it offered five hidden legacy themes and omitted
// the published ones (ember / blue-slate / parchment / terminals / system).

/** One row in the flattened, keyboard-navigable list. Either a header (skipped by the
 *  arrow keys) or a selectable item that runs `onSelect`. */
type Row =
  | { kind: 'header'; key: string; label: string }
  | { kind: 'item'; key: string; group: string; label: string; hint?: string; onSelect: () => void };

export function CommandPalette(): JSX.Element | null {
  const mount = useShellMountSnapshot();
  const navigate = useNavigate();
  const open = useUiStore((s) => s.commandPaletteOpen);
  const setOpen = useUiStore((s) => s.setCommandPaletteOpen);
  const setTheme = useUiStore((s) => s.setTheme);
  const toggleNav = useUiStore((s) => s.toggleNav);
  const pushToast = useToastStore((s) => s.push);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // The global ⌘K / Ctrl-K toggle + Escape-to-close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!open);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  // Reset query + focus the input each time the palette opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
      setActiveIndex(0);
      // Defer focus to after the element is in the DOM.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Debounce the query that drives the live corpus queries so we don't fire a request
  // per keystroke. The command list still filters synchronously off `query`.
  useEffect(() => {
    const trimmed = query.trim();
    const handle = setTimeout(() => setDebounced(trimmed), LIVE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  const liveEnabled = open && debounced.length > 0;

  // --- live corpus queries (enabled only when the palette is open + query non-empty) ----

  const notesQuery = useQuery({
    queryKey: ['commandPalette.noteTitles', LIVE_FETCH_LIMIT],
    queryFn: () => noteTitles({ limit: LIVE_FETCH_LIMIT }),
    enabled: open, // titles are cheap + reused; filter client-side per keystroke
    staleTime: 60_000,
  });

  const tagsQuery = useQuery({
    queryKey: ['commandPalette.tags', debounced],
    queryFn: () => listTags({ prefix: debounced, limit: RESULTS_PER_GROUP }),
    enabled: liveEnabled,
    staleTime: 30_000,
  });

  const entitiesQuery = useQuery({
    queryKey: ['commandPalette.entities', debounced],
    queryFn: () => listEntities({ prefix: debounced, limit: RESULTS_PER_GROUP }),
    enabled: liveEnabled,
    staleTime: 30_000,
  });

  // --- the static + dynamic commands (independent of the registered nav commands) -------

  const staticCommands = useMemo<PaletteCommand[]>(() => {
    const cmds: PaletteCommand[] = [
      {
        id: 'action.newNote',
        label: 'New note',
        group: 'Actions',
        keywords: ['create', 'note', 'editor', 'write'],
        run: () => navigate(toEditor()),
      },
      {
        id: 'action.newChat',
        label: 'New chat',
        group: 'Actions',
        keywords: ['chat', 'conversation', 'ask'],
        run: () => navigate(ROUTES.chat),
      },
      {
        id: 'action.clearChatHistory',
        label: 'Clear chat history',
        group: 'Actions',
        keywords: ['chat', 'delete', 'reset', 'sessions'],
        run: () => {
          // Destructive + irreversible — gate behind a native confirm so the palette
          // never silently nukes history (the Chat surface has its own richer modal).
          const ok =
            typeof window === 'undefined'
              ? false
              : window.confirm(
                  'Clear all chat history? This permanently deletes every chat session and ' +
                    'its messages. This cannot be undone.',
                );
          if (!ok) return;
          void clearChatHistory()
            .then((r) => {
              pushToast({
                kind: 'success',
                title: 'Chat history cleared',
                message: `Deleted ${r.cleared} session${r.cleared === 1 ? '' : 's'}.`,
              });
            })
            .catch(() => {
              pushToast({
                kind: 'error',
                title: 'Could not clear chat history',
                message: 'The request failed. Try again from the Chat surface.',
              });
            });
        },
      },
      {
        id: 'action.toggleSidebar',
        label: 'Toggle sidebar',
        group: 'Actions',
        keywords: ['nav', 'collapse', 'expand', 'sidebar'],
        run: () => toggleNav(),
      },
    ];
    for (const opt of THEME_CATALOG) {
      cmds.push({
        id: `theme.${opt.id}`,
        label: `Theme: ${opt.label}`,
        group: 'Theme',
        keywords: ['theme', 'appearance', 'color', opt.label.toLowerCase()],
        run: () => setTheme(opt.id),
      });
    }
    return cmds;
  }, [navigate, setTheme, toggleNav, pushToast]);

  // The dynamic "Search the corpus for '<query>'" command — only meaningful with a query.
  const searchCorpusCommand = useMemo<PaletteCommand | null>(() => {
    const q = query.trim();
    if (q.length === 0) return null;
    return {
      id: 'action.searchCorpus',
      label: `Search the corpus for “${q}”`,
      group: 'Actions',
      keywords: ['search', 'find', 'corpus', q],
      run: () => navigate(toSearch(`q=${encodeURIComponent(q)}`)),
    };
  }, [query, navigate]);

  // Matched + sorted commands (registered nav commands + our static + the dynamic search).
  const matchedCommands = useMemo<PaletteCommand[]>(() => {
    const all = [...mount.getCommands(), ...staticCommands];
    if (searchCorpusCommand) all.push(searchCorpusCommand);
    if (query.trim().length === 0) {
      // No query — show the static set in registration order (no live results yet).
      return all;
    }
    return all
      .map((cmd) => ({ cmd, score: commandMatchScore(cmd, query) }))
      .filter((r) => r.score >= 0)
      .sort((a, b) => b.score - a.score)
      .map((r) => r.cmd);
  }, [mount, staticCommands, searchCorpusCommand, query]);

  // --- live-result rows (filtered + capped) --------------------------------------------

  const noteRows = useMemo(() => {
    if (debounced.length === 0) return [];
    const items = notesQuery.data?.data.items ?? [];
    const q = debounced.toLowerCase();
    return items
      .filter((it) => it.title.toLowerCase().includes(q))
      .slice(0, RESULTS_PER_GROUP);
  }, [notesQuery.data, debounced]);

  const tagItems = useMemo(() => {
    if (debounced.length === 0) return [];
    return (tagsQuery.data?.data.items ?? []).slice(0, RESULTS_PER_GROUP);
  }, [tagsQuery.data, debounced]);

  const entityItems = useMemo(() => {
    if (debounced.length === 0) return [];
    return (entitiesQuery.data?.data.items ?? []).slice(0, RESULTS_PER_GROUP);
  }, [entitiesQuery.data, debounced]);

  // --- flatten everything into the keyboard-navigable row list -------------------------

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    const close = (fn: () => void) => () => {
      setOpen(false);
      fn();
    };

    if (matchedCommands.length > 0) {
      out.push({ kind: 'header', key: 'h.commands', label: 'Commands' });
      for (const cmd of matchedCommands) {
        out.push({
          kind: 'item',
          key: `cmd.${cmd.id}`,
          group: cmd.group ?? 'Commands',
          label: cmd.label,
          onSelect: close(() => cmd.run()),
        });
      }
    }

    if (noteRows.length > 0) {
      out.push({ kind: 'header', key: 'h.notes', label: 'Notes' });
      for (const n of noteRows) {
        out.push({
          kind: 'item',
          key: `note.${n.noteId}`,
          group: 'Notes',
          label: n.title || 'Untitled',
          onSelect: close(() => navigate(toEditor(n.noteId))),
        });
      }
    }

    if (tagItems.length > 0) {
      out.push({ kind: 'header', key: 'h.tags', label: 'Tags' });
      for (const t of tagItems) {
        out.push({
          kind: 'item',
          key: `tag.${t.tagId}`,
          group: 'Tags',
          label: t.display || t.path,
          hint: `${t.noteCount}`,
          onSelect: close(() => navigate(toSearchTag(t.path))),
        });
      }
    }

    if (entityItems.length > 0) {
      out.push({ kind: 'header', key: 'h.entities', label: 'Entities' });
      for (const e of entityItems) {
        out.push({
          kind: 'item',
          key: `entity.${e.entityId}`,
          group: 'Entities',
          label: e.canonical,
          hint: e.type,
          onSelect: close(() => navigate(toSearch(`entity=${encodeURIComponent(e.entityId)}`))),
        });
      }
    }

    return out;
  }, [matchedCommands, noteRows, tagItems, entityItems, navigate, setOpen]);

  // The indices of selectable (non-header) rows, for arrow-key navigation.
  const selectableIndices = useMemo(
    () => rows.map((r, i) => (r.kind === 'item' ? i : -1)).filter((i) => i >= 0),
    [rows],
  );

  // Keep the active index pointed at a selectable row whenever the list changes.
  useEffect(() => {
    if (selectableIndices.length === 0) {
      setActiveIndex(0);
      return;
    }
    if (!selectableIndices.includes(activeIndex)) {
      setActiveIndex(selectableIndices[0] as number);
    }
  }, [selectableIndices, activeIndex]);

  if (!open) return null;

  const isLoading = liveEnabled && (notesQuery.isFetching || tagsQuery.isFetching || entitiesQuery.isFetching);
  const isEmpty = rows.length === 0;

  const moveActive = (dir: 1 | -1): void => {
    if (selectableIndices.length === 0) return;
    const pos = selectableIndices.indexOf(activeIndex);
    const nextPos =
      pos === -1
        ? 0
        : Math.min(Math.max(pos + dir, 0), selectableIndices.length - 1);
    setActiveIndex(selectableIndices[nextPos] as number);
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveActive(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[activeIndex];
      if (row && row.kind === 'item') row.onSelect();
    }
  };

  return (
    <div
      className="command-palette__backdrop"
      role="presentation"
      onClick={() => setOpen(false)}
    >
      <div
        className="command-palette"
        role="dialog"
        aria-label="Command palette"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          className="command-palette__input"
          type="text"
          placeholder="Search commands, notes, tags, entities…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={onInputKeyDown}
          aria-label="Command palette input"
        />
        <ul className="command-palette__results" role="listbox">
          {isEmpty && !isLoading && (
            <li className="command-palette__empty">No matching commands or results</li>
          )}
          {isEmpty && isLoading && (
            <li className="command-palette__empty">Searching…</li>
          )}
          {rows.map((row, index) =>
            row.kind === 'header' ? (
              <li key={row.key} className="command-palette__group" role="presentation">
                {row.label}
              </li>
            ) : (
              <li
                key={row.key}
                role="option"
                aria-selected={index === activeIndex}
                className={`command-palette__result ${
                  index === activeIndex ? 'command-palette__result--active' : ''
                }`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => row.onSelect()}
              >
                <span className="command-palette__label">{row.label}</span>
                {row.hint !== undefined && (
                  <span className="command-palette__group">{row.hint}</span>
                )}
              </li>
            ),
          )}
        </ul>
      </div>
    </div>
  );
}
